import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  Compass,
  Calendar,
  ChevronRight,
  Smile,
  Meh,
  Frown,
  Heart,
  Angry,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const moodIcons = {
  great: { icon: Heart, color: "text-green-400" },
  good: { icon: Smile, color: "text-[#C4B5FD]" },
  okay: { icon: Meh, color: "text-[#D4AF37]" },
  low: { icon: Frown, color: "text-orange-400" },
  difficult: { icon: Angry, color: "text-red-400" },
};

const HistoryPage = () => {
  const navigate = useNavigate();
  const [guidanceHistory, setGuidanceHistory] = useState([]);
  const [reflectionHistory, setReflectionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGuidance, setSelectedGuidance] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const [guidanceRes, reflectionRes] = await Promise.all([
          api.get("/guidance/history"),
          api.get("/reflection/history"),
        ]);
        setGuidanceHistory(guidanceRes.data);
        setReflectionHistory(reflectionRes.data);
      } catch (error) {
        console.error("Error fetching history:", error);
        toast.error("Failed to load history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const getReflectionForDate = (date) => {
    return reflectionHistory.find((r) => r.date === date);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="history-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white">History</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Title */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-display text-3xl text-white mb-2">Your Journey</h1>
          <p className="text-white/60 font-body">Review your past guidance and reflections</p>
        </div>

        {guidanceHistory.length === 0 ? (
          <div className="glass-card p-8 text-center animate-fade-in-up">
            <Calendar className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <h3 className="font-display text-xl text-white mb-2">No history yet</h3>
            <p className="text-white/60 font-body mb-6">
              Your daily guidance and reflections will appear here
            </p>
            <Button onClick={() => navigate("/dashboard")} className="btn-gold">
              Get Today's Guidance
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-250px)]">
            <div className="space-y-3">
              {guidanceHistory.map((guidance, index) => {
                const reflection = getReflectionForDate(guidance.date);
                const MoodIcon = reflection ? moodIcons[reflection.mood]?.icon : null;
                const moodColor = reflection ? moodIcons[reflection.mood]?.color : "";

                return (
                  <button
                    key={guidance.guidance_id}
                    data-testid={`history-item-${index}`}
                    onClick={() => setSelectedGuidance(guidance)}
                    className="w-full glass-card p-4 sm:p-5 flex items-center gap-4 hover:bg-white/[0.06] transition-all duration-300 animate-fade-in-up text-left"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {/* Date */}
                    <div className="flex-shrink-0 w-14 text-center">
                      <div className="text-xs text-[#D4AF37] uppercase font-body">
                        {new Date(guidance.date).toLocaleDateString("en-US", { month: "short" })}
                      </div>
                      <div className="text-2xl font-display text-white">
                        {new Date(guidance.date).getDate()}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-white truncate">{guidance.headline}</h3>
                      <p className="text-white/50 text-sm font-body truncate">{guidance.overview}</p>
                    </div>

                    {/* Reflection Status */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {reflection ? (
                        <>
                          {MoodIcon && <MoodIcon className={`w-5 h-5 ${moodColor}`} />}
                          {reflection.guidance_helped ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <X className="w-4 h-4 text-orange-400" />
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-white/30 font-body">No reflection</span>
                      )}
                      <ChevronRight className="w-5 h-5 text-white/30" />
                    </div>
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </main>

      {/* Guidance Detail Dialog */}
      <Dialog open={!!selectedGuidance} onOpenChange={() => setSelectedGuidance(null)}>
        <DialogContent className="bg-[#0A101D] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {selectedGuidance?.headline}
            </DialogTitle>
            <p className="text-[#D4AF37] text-sm font-body">
              {selectedGuidance && formatDate(selectedGuidance.date)}
            </p>
          </DialogHeader>

          {selectedGuidance && (
            <div className="space-y-6 mt-4">
              {/* Overview */}
              <div>
                <p className="text-white/80 font-body leading-relaxed">{selectedGuidance.overview}</p>
              </div>

              {/* Do & Avoid */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-green-400 font-body font-medium mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4" /> Do Today
                  </h4>
                  <ul className="space-y-2">
                    {selectedGuidance.do_today.map((item, idx) => (
                      <li key={idx} className="text-white/70 text-sm font-body flex items-start gap-2">
                        <span className="w-1 h-1 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <h4 className="text-red-400 font-body font-medium mb-3 flex items-center gap-2">
                    <X className="w-4 h-4" /> Avoid Today
                  </h4>
                  <ul className="space-y-2">
                    {selectedGuidance.avoid_today.map((item, idx) => (
                      <li key={idx} className="text-white/70 text-sm font-body flex items-start gap-2">
                        <span className="w-1 h-1 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Wise Saying */}
              <div className="gold-accent-card p-4">
                <p className="font-display text-lg text-white italic">"{selectedGuidance.wise_saying}"</p>
              </div>

              {/* Spiritual Guidance */}
              <div>
                <h4 className="text-[#C4B5FD] font-body font-medium mb-2">Spiritual Insight</h4>
                <p className="text-white/70 font-body text-sm">{selectedGuidance.spiritual_guidance}</p>
              </div>

              {/* Reflection for this date */}
              {(() => {
                const reflection = getReflectionForDate(selectedGuidance.date);
                if (!reflection) return null;
                const MoodIcon = moodIcons[reflection.mood]?.icon;
                const moodColor = moodIcons[reflection.mood]?.color;
                return (
                  <div className="bg-[#8B5CF6]/10 rounded-xl p-4 border border-[#8B5CF6]/30">
                    <h4 className="text-[#C4B5FD] font-body font-medium mb-3">Your Reflection</h4>
                    <div className="flex items-center gap-4 mb-2">
                      {MoodIcon && <MoodIcon className={`w-6 h-6 ${moodColor}`} />}
                      <span className="text-white/80 font-body capitalize">{reflection.mood}</span>
                      <span className={`text-sm ${reflection.guidance_helped ? "text-green-400" : "text-orange-400"}`}>
                        {reflection.guidance_helped ? "Guidance helped" : "Guidance didn't help"}
                      </span>
                    </div>
                    {reflection.notes && (
                      <p className="text-white/60 text-sm font-body mt-2">{reflection.notes}</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistoryPage;
